import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Filter, Search, Users, Layers, Minimize2, Grid3X3, LayoutGrid, ChevronDown, ChevronUp, Sparkles, Maximize2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ConsultantReviewPanel } from './ConsultantReviewPanel';
import { DynamicWizard } from './DynamicWizard';
import { GuidedMode } from './GuidedMode';
import { DiscoveryGrid, SummaryBand, LENS_HINTS, type GridDensity, type GridLens } from './DiscoveryGrid';
import { DiscoveryCellDrawer } from './DiscoveryCellDrawer';
import { DiscoveryQuestionsPanel } from './DiscoveryQuestionsPanel';
import { ScopeReconcileDialog } from './ScopeReconcileDialog';
import type { HierarchicalConfigItem } from '../types/phase1e';
import { toast } from 'sonner';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { useFocusedEngagement } from '../hooks/useFocusedEngagement';
import { useDiscoveryGrid } from '../hooks/useDiscoveryGrid';
import { cellPatchFor, type CellChange } from '../utils/discoveryGridPatch';
import { readGridViewPrefs, resolveDensity, writeGridViewPrefs, type DensitySetting } from '../utils/discoveryGridView';
import { LEVEL_LABELS, OWNER_LABELS, type CellLevel, type GridBlock, type GridCell, type GridRegion } from '../utils/discoveryGrid';
import { normalizeBallInCourtFilter, type BallInCourtFilter } from '../utils/discoveryGridStatus';
import { checkCell, getConfigItem } from '../utils/discoveryGridApi';
import { isClientRoleName } from '../utils/clientRoles';
import { getActiveViewRole } from '../utils/roleUtils';

/**
 * Discovery grid host (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4, §5).
 *
 * Loads the grid payload from GET /discovery-grid/:companyId for the focused
 * engagement, renders it with <DiscoveryGrid>, owns the filters (which dim,
 * never hide) and the lens, opens the cell drawer on click, and from the drawer
 * opens the work: the review panel at a question, Guided Mode at a category
 * question, a wizard in place, or a configuration route. `?cell=<id>` in the
 * URL selects a cell on load and is written on every click (deep links, §4.8).
 */

interface ClientConfigItemsTableProps {
  items: HierarchicalConfigItem[];
  spaces?: { id: string; name: string }[];
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  onUpdate: () => void;
  selectedCategory?: string | null;
  statusFilter?: string;
  onStatusFilterChange?: (filter: string) => void;
  ballInCourtFilter?: string;
  onBallInCourtFilterChange?: (filter: string) => void;
  initialOpenItemId?: string;
  initialOpenTab?: string;
  /** Cell to select (and open the drawer for) on load; `?cell=` in the URL also works. */
  initialCellId?: string | null;
  changeRequests?: any[];
  isModal?: boolean;
  onCloseModal?: () => void;
  sows?: any[];
  /** Focused engagement chosen by the parent (overrides the hook's own resolution). */
  engagementId?: string | null;
  /** A space's page: draw only the cells of config items in this space (GET /discovery-grid/:companyId?spaceId=&spaceType=space). */
  spaceId?: string | null;
}

type LevelFilter = 'all' | 'blocked' | `${CellLevel}`;

/** Map the status-filter values parents pass (the lifecycle statuses and the legacy spellings) to a level filter. */
function levelFilterFromStatus(status: string | undefined): LevelFilter {
  switch (status) {
    case 'not_started': return '0';
    case 'waiting_consultant_response': case 'in_progress': return '2';
    case 'completed': return '3';
    case 'ready_for_build': case 'in_build': case 'ready_for_review': case 'approved': return '5';
    case 'discovery': return 'all';
    case 'blocked': return 'blocked';
    default:
      return status && /^[0-5]$/.test(status) ? (status as LevelFilter) : 'all';
  }
}

function readCellParam(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('cell');
  } catch {
    return null;
  }
}

function writeCellParam(cellId: string | null) {
  try {
    const url = new URL(window.location.href);
    if (cellId) url.searchParams.set('cell', cellId);
    else url.searchParams.delete('cell');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // ignore
  }
}

export function ClientConfigItemsTable({
  items,
  spaces = [],
  companyId,
  companyName,
  accessToken,
  currentUser,
  onUpdate,
  selectedCategory,
  statusFilter,
  onStatusFilterChange,
  ballInCourtFilter,
  onBallInCourtFilterChange,
  initialOpenItemId,
  initialOpenTab,
  initialCellId,
  isModal = false,
  onCloseModal,
  sows = [],
  engagementId: engagementIdProp = null,
  spaceId = null,
}: ClientConfigItemsTableProps) {
  const navigate = useNavigate();

  // Focused engagement (PRD §4.7): a parent-provided id wins; otherwise URL →
  // per-user preference → only → latest active → latest created.
  const { focusedEngagement: hookFocusedEngagement } = useFocusedEngagement({ engagements: sows, companyId, userId: currentUser?.id });
  const focusedSow = useMemo(() => {
    if (engagementIdProp) return sows.find((s: any) => s.id === engagementIdProp) ?? hookFocusedEngagement;
    return hookFocusedEngagement;
  }, [engagementIdProp, sows, hookFocusedEngagement]);
  const focusedEngagementId = engagementIdProp ?? focusedSow?.id ?? null;

  const { data, loading, error, reload, patchCell, refreshing } = useDiscoveryGrid({ companyId, engagementId: focusedEngagementId, accessToken, spaceId, spaceType: spaceId ? 'space' : null });

  // View As a client role shows the client drawer even though the server sees the real (firm or system admin) account.
  const viewingAsClient = isClientRoleName(getActiveViewRole(currentUser));
  const viewerRole: 'consultant' | 'client' = viewingAsClient ? 'client' : (data?.viewerRole ?? (['client', 'user', 'company_admin', 'company_user'].includes(currentUser?.role) ? 'client' : 'consultant'));
  const viewerId: string | undefined = data?.viewerId ?? currentUser?.id;

  // --- Lens, density, filters (filters dim, never hide) ---
  const [lens, setLens] = useState<GridLens>('progress');
  const lensDefaulted = useRef(false);
  useEffect(() => {
    // Clients with assignments start on Mine (spec §4.9); everyone else on Progress. Once.
    if (lensDefaulted.current || !data) return;
    lensDefaulted.current = true;
    if (viewerRole === 'client' && viewerId && data.regions.some((r) => r.blocks.some((b) => b.assignedTo === viewerId))) setLens('mine');
  }, [data, viewerRole, viewerId]);

  // Density and legend state are remembered per browser (utils/discoveryGridView.ts).
  const [viewPrefs, setViewPrefs] = useState(() => readGridViewPrefs());
  useEffect(() => { writeGridViewPrefs(viewPrefs); }, [viewPrefs]);
  const density: GridDensity = resolveDensity(viewPrefs.density, data?.summary.total ?? 0);
  const setDensitySetting = (d: DensitySetting) => setViewPrefs((p) => ({ ...p, density: d }));
  const legendCollapsed = viewPrefs.legendCollapsed;
  const setLegendCollapsed = (v: boolean) => setViewPrefs((p) => ({ ...p, legendCollapsed: v }));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState<LevelFilter>(levelFilterFromStatus(statusFilter));
  const [filterBallInCourt, setFilterBallInCourt] = useState<BallInCourtFilter>(normalizeBallInCourtFilter(ballInCourtFilter));
  const [filterCategory, setFilterCategory] = useState<string>(selectedCategory || 'all');
  const [filterSpace, setFilterSpace] = useState<string>('all');

  useEffect(() => { setFilterLevel(levelFilterFromStatus(statusFilter)); }, [statusFilter]);
  useEffect(() => { setFilterBallInCourt(normalizeBallInCourtFilter(ballInCourtFilter)); }, [ballInCourtFilter]);
  useEffect(() => { if (selectedCategory) setFilterCategory(selectedCategory); }, [selectedCategory]);

  const hasActiveFilter = !!searchTerm || filterLevel !== 'all' || filterBallInCourt !== 'all' || filterCategory !== 'all' || filterSpace !== 'all';
  const matches = useMemo(() => {
    if (!hasActiveFilter) return undefined;
    const term = searchTerm.trim().toLowerCase();
    return (cell: GridCell, block: GridBlock, region: GridRegion) => {
      if (term && !cell.text.toLowerCase().includes(term) && !block.name.toLowerCase().includes(term)) return false;
      if (filterLevel === 'blocked') { if (!cell.blocked) return false; }
      else if (filterLevel !== 'all' && String(cell.level) !== filterLevel) return false;
      if (filterBallInCourt !== 'all' && cell.owner !== filterBallInCourt) return false;
      if (filterCategory !== 'all' && region.category !== filterCategory) return false;
      if (filterSpace !== 'all' && block.spaceId !== filterSpace && !block.isGlobal) return false;
      return true;
    };
  }, [hasActiveFilter, searchTerm, filterLevel, filterBallInCourt, filterCategory, filterSpace]);

  const categories = useMemo(() => (data?.regions ?? []).map((r) => r.category), [data]);

  // --- Selection, drawer, and the work surfaces ---
  const [selected, setSelected] = useState<{ cell: GridCell; block: GridBlock; category: string } | null>(null);
  const [reviewItem, setReviewItem] = useState<HierarchicalConfigItem | null>(null);
  const [reviewQuestionId, setReviewQuestionId] = useState<string | undefined>(undefined);
  const [reviewPanelViewMode, setReviewPanelViewMode] = useState<'answers' | 'activity' | 'ai-tools' | 'discovery-console' | 'scope-review'>('answers');
  const [wizardTarget, setWizardTarget] = useState<{ configItemId: string; wizardId: string } | null>(null);
  const [guidedCategory, setGuidedCategory] = useState<string | null>(null);
  const [guidedFocusQuestionId, setGuidedFocusQuestionId] = useState<string | null>(null);
  const [showNotActionable, setShowNotActionable] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  // Clients answer ordinary config-item questions here, never in the consultant review console.
  const [clientQuestionItem, setClientQuestionItem] = useState<HierarchicalConfigItem | null>(null);

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  // An action on the selected cell succeeded: show its expected result now (level, owner,
  // counts), then let the reload replace it with the server's derivation.
  // `change` is known for drawer actions (optimistic patch); dialogs that may have changed
  // anything (Guided Mode, wizards, the review panel, reconciliation) pass nothing and just reload.
  const afterChange = useCallback((change?: CellChange) => {
    if (change && selected) patchCell(selected.cell.id, cellPatchFor(selected.cell, change));
    reload();
    onUpdate();
  }, [selected, patchCell, reload, onUpdate]);

  // Keep the selected cell's facts current after a reload (level changes after check / follow-up).
  useEffect(() => {
    if (!selected || !data) return;
    for (const r of data.regions) for (const b of r.blocks) for (const c of b.cells) {
      if (c.id === selected.cell.id) {
        if (c !== selected.cell) setSelected({ cell: c, block: b, category: r.category });
        return;
      }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectCell = useCallback((cell: GridCell, block: GridBlock, region: GridRegion) => {
    setSelected({ cell, block, category: region.category });
    writeCellParam(cell.id);
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(null);
    writeCellParam(null);
  }, []);

  // Deep link: ?cell= (or the initialCellId prop) selects a cell once the grid is loaded.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || !data) return;
    const wanted = initialCellId || readCellParam();
    if (!wanted) { deepLinked.current = true; return; }
    for (const r of data.regions) for (const b of r.blocks) for (const c of b.cells) {
      if (c.id === wanted) {
        deepLinked.current = true;
        setSelected({ cell: c, block: b, category: r.category });
        return;
      }
    }
    deepLinked.current = true;
  }, [data, initialCellId]);

  /** Open the full surface for a cell. A consultant opening an unchecked answer marks it checked (spec §4.2). */
  const openWork = useCallback(async (cell: GridCell, block: GridBlock, opts?: { edit?: boolean }) => {
    // Opening to look marks the answer checked; opening to edit does not (the edit resets it anyway).
    if (!opts?.edit && viewerRole === 'consultant' && cell.kind === 'question' && cell.level === 3) {
      checkCell(companyId, cell.id, accessToken)
        .then(() => { patchCell(cell.id, cellPatchFor(cell, { kind: 'checked' })); reload(); })
        .catch(() => { /* non-fatal */ });
    }
    if (cell.open.type === 'wizard' && cell.open.wizardId && cell.open.configItemId) {
      setWizardTarget({ configItemId: cell.open.configItemId, wizardId: cell.open.wizardId });
      return;
    }
    if (cell.open.type === 'route') {
      // Route cells lead to consultant screens (space review, dashboards, work types).
      if (viewerRole === 'client') {
        toast.info('Your consultant sets this part up. Nothing is needed from you here.');
        return;
      }
      if (cell.open.path) navigate(cell.open.path);
      else toast.info('This artifact has no configuration screen yet');
      return;
    }
    if (cell.open.type === 'guided' && cell.open.category) {
      setGuidedFocusQuestionId(opts?.edit ? cell.questionId ?? null : null);
      setGuidedCategory(cell.open.category);
      return;
    }
    // The grid is built from the per-item store; the list this table was given (the client page's
    // older company list) may not hold the item, so load it by id before giving up.
    let item: HierarchicalConfigItem | undefined = itemById.get(block.configItemId);
    if (!item) item = await getConfigItem(companyId, block.configItemId, accessToken).catch(() => undefined) ?? undefined;
    if (!item) {
      toast.error(viewerRole === 'client' ? 'This question is not open for answers yet. Your consultant will let you know.' : `Open "${block.name}" from the config item list to answer this question`);
      return;
    }
    if (viewerRole === 'client') {
      setClientQuestionItem(item);
      return;
    }
    setReviewItem(item);
    setReviewQuestionId(cell.open.questionId);
    setReviewPanelViewMode('answers');
  }, [viewerRole, companyId, accessToken, reload, patchCell, navigate, itemById]);

  // Deep link from a parent: open the review panel on a config item (and tab).
  useEffect(() => {
    if (!initialOpenItemId) return;
    const item = itemById.get(initialOpenItemId);
    if (!item) return;
    setReviewItem(item);
    setReviewQuestionId(undefined);
    setReviewPanelViewMode((initialOpenTab as any) || 'answers');
  }, [initialOpenItemId, initialOpenTab, itemById]);

  const closeReview = () => { setReviewItem(null); setReviewQuestionId(undefined); };

  const lensButton = (value: GridLens, label: string) => (
    <Button
      key={value}
      variant="ghost"
      size="sm"
      onClick={() => setLens(value)}
      aria-pressed={lens === value}
      className={`h-7 px-2.5 text-xs ${lens === value ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
    >
      {label}
    </Button>
  );

  const header = (
    <div className={`flex flex-col gap-4 border-b bg-white px-6 py-5 ${isModal ? 'sticky top-0 z-40' : ''}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className={legendCollapsed ? 'flex items-baseline gap-3 flex-wrap min-w-0' : 'min-w-[260px] flex-1'}>
          <h2 className={legendCollapsed ? 'text-lg font-bold text-gray-900 tracking-tight' : 'text-2xl font-bold text-gray-900 tracking-tight'}>Discovery Grid</h2>
          {legendCollapsed && data?.summary && (
            <span className="text-xs text-gray-600 tabular-nums">
              {data.summary.total} questions and artifacts · <span className="text-amber-700 font-semibold">{viewerRole === 'client' ? data.summary.court.client : data.summary.court.consultant}</span> waiting on you · {viewerRole === 'client' ? data.summary.court.consultant : data.summary.court.client} waiting on the {viewerRole === 'client' ? 'consultant' : 'client'} · {data.summary.percentLocked}% locked{data.summary.blocked > 0 ? ` · ${data.summary.blocked} blocked` : ''}
            </span>
          )}
          {!legendCollapsed && <p className="text-sm text-gray-500 mt-1 max-w-xl">Everything {spaceId ? 'this space' : 'this engagement'} needs to know before build: one square per question, one circle per wizard or configuration. Darker is more settled. Click a cell to work on it.</p>}
          {!legendCollapsed && (companyName || focusedSow?.name) && <p className="text-xs text-gray-400 mt-1">{companyName}{focusedSow?.name ? ` · ${focusedSow.name}` : ''}</p>}
        </div>
        {!legendCollapsed && data?.summary && <SummaryBand summary={data.summary} viewerRole={viewerRole} lens={lens} />}
        <Button variant="ghost" size="sm" className="h-7 text-xs text-gray-500 hover:text-gray-800" onClick={() => setLegendCollapsed(!legendCollapsed)} aria-expanded={!legendCollapsed}>
          {legendCollapsed ? <ChevronDown className="size-3.5 mr-1" /> : <ChevronUp className="size-3.5 mr-1" />}
          {legendCollapsed ? 'Show legend' : 'Hide legend'}
        </Button>
        {isModal && onCloseModal && (
          <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full hover:bg-gray-100 -mr-2" onClick={onCloseModal}>
            <Minimize2 className="h-6 w-6 text-gray-500" />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200" role="group" aria-label="Lens">
          {lensButton('progress', 'Progress')}
          {lensButton('court', 'Ball in court')}
          {lensButton('mine', 'Mine')}
          {lensButton('source', 'Scope source')}
        </div>
        {!legendCollapsed && <span className="text-[11px] text-gray-500 max-w-md">{LENS_HINTS[lens]}</span>}
        {viewerRole === 'consultant' && focusedEngagementId && sows.length > 1 && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowReconcile(true)}>Carry forward from a previous engagement…</Button>
        )}
        <div className="ml-auto flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200" role="group" aria-label="Density" title={viewPrefs.density === 'auto' ? `Auto: ${density} for ${data?.summary.total ?? 0} cells` : undefined}>
          {([
            ['auto', 'Auto', Sparkles],
            ['compact', 'Compact', Grid3X3],
            ['comfortable', 'Comfortable', LayoutGrid],
            ['spacious', 'Spacious', Maximize2],
          ] as const).map(([value, label, Icon]) => (
            <Button key={value} variant="ghost" size="sm" onClick={() => setDensitySetting(value)} className={`h-7 px-2.5 text-xs ${viewPrefs.density === value ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`} aria-pressed={viewPrefs.density === value}>
              <Icon className="size-3.5 mr-1.5" />
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search questions and config items..." className="pl-8 h-8 text-xs bg-gray-50 border-gray-200 focus:bg-white transition-colors" />
        </div>

        {spaces.length > 1 && (
          <Select value={filterSpace} onValueChange={setFilterSpace}>
            <SelectTrigger className="w-[160px] h-8 text-xs border-dashed border-gray-300 bg-transparent hover:bg-gray-50">
              <Layers className="size-3 mr-2 text-purple-600" />
              <SelectValue placeholder="All Spaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Spaces (Aggregate)</SelectItem>
              {spaces.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={filterLevel} onValueChange={(val) => { setFilterLevel(val as LevelFilter); onStatusFilterChange?.(val === 'all' ? '' : val); }}>
          <SelectTrigger className="w-[160px] h-8 text-xs border-dashed border-gray-300 bg-transparent hover:bg-gray-50" aria-label="Level">
            <Filter className="size-3 mr-2 text-gray-500" />
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            {([0, 1, 2, 3, 4, 5] as CellLevel[]).map((l) => <SelectItem key={l} value={String(l)}>{l} · {LEVEL_LABELS[l]}</SelectItem>)}
            <SelectItem value="blocked">Blocked</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterBallInCourt} onValueChange={(val) => { const next = normalizeBallInCourtFilter(val); setFilterBallInCourt(next); onBallInCourtFilterChange?.(next === 'all' ? '' : next); }}>
          <SelectTrigger className="w-[150px] h-8 text-xs border-dashed border-gray-300 bg-transparent hover:bg-gray-50" aria-label="Ball in court">
            <Users className="size-3 mr-2 text-gray-500" />
            <SelectValue placeholder="Ball in court" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any court</SelectItem>
            <SelectItem value="client">{OWNER_LABELS.client}</SelectItem>
            <SelectItem value="consultant">{OWNER_LABELS.consultant}</SelectItem>
            <SelectItem value="none">{OWNER_LABELS.none}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[150px] h-8 text-xs border-dashed border-gray-300 bg-transparent hover:bg-gray-50" aria-label="Category">
            <Layers className="size-3 mr-2 text-gray-500" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{formatCategoryLabel(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const guidedItems = useMemo(() => (guidedCategory ? items.filter((i) => i.category === guidedCategory) : []), [items, guidedCategory]);

  return (
    <div className="flex flex-col h-full w-full bg-white relative">
      {header}
      <div className="flex-1 flex overflow-hidden">
        {/* The grid grows 1×, the drawer 3× (up to its max), so a small engagement gives the drawer the room
            the grid does not need; a big grid (wide max-content) keeps the drawer at its 360px minimum. */}
        <div className="flex-[1_1_auto] min-w-0 overflow-y-auto p-6 bg-gray-50/30 relative">
          <DiscoveryGrid
            data={data}
            loading={loading}
            error={error}
            density={density}
            lens={lens}
            viewerId={viewerId}
            matches={matches}
            selectedCellId={selected?.cell.id ?? null}
            onCellClick={selectCell}
            viewerRole={viewerRole}
            notActionableCount={viewerRole === 'client' ? 0 : (data?.notActionable.length ?? 0)}
            onShowNotActionable={() => setShowNotActionable(true)}
          />
        </div>
        {selected && (
          <DiscoveryCellDrawer
            companyId={companyId}
            engagementId={focusedEngagementId}
            accessToken={accessToken}
            viewerRole={viewerRole}
            viewer={{ id: currentUser?.id, name: currentUser?.name || currentUser?.email }}
            cell={selected.cell}
            block={selected.block}
            category={selected.category}
            onClose={clearSelection}
            onOpenWork={openWork}
            onChanged={afterChange}
            refreshing={refreshing}
          />
        )}
      </div>

      <Dialog open={showNotActionable} onOpenChange={setShowNotActionable}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Config items without discovery cells</DialogTitle>
            <DialogDescription>These items are in scope but have no asked questions or artifact yet, so they are not drawn on the grid.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {(data?.notActionable ?? []).map((n) => (
              <div key={n.configItemId} className="p-3 border rounded bg-slate-50 flex justify-between items-center">
                <span className="font-medium text-sm">{n.name}</span>
                <Badge variant="outline">{formatCategoryLabel(n.category)}</Badge>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {clientQuestionItem && (
        <Dialog open onOpenChange={(open) => { if (!open) { setClientQuestionItem(null); afterChange(); } }}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{clientQuestionItem.name}</DialogTitle>
              <DialogDescription>Answer what you can. Your consultant sees your answers right away.</DialogDescription>
            </DialogHeader>
            <DiscoveryQuestionsPanel
              configItemId={clientQuestionItem.id}
              configItemName={clientQuestionItem.name}
              companyId={companyId}
              sowId={focusedSow?.id}
              accessToken={accessToken}
              isConsultant={false}
              currentUser={currentUser}
              configItemCategory={clientQuestionItem.category}
              consultingFirmId={currentUser?.consultingFirmId || currentUser?.firmId}
            />
          </DialogContent>
        </Dialog>
      )}

      {reviewItem && (
        <ConsultantReviewPanel
          open
          onClose={closeReview}
          item={reviewItem}
          initialQuestionId={reviewQuestionId}
          initialViewMode={reviewPanelViewMode}
          companyId={companyId}
          companyName={companyName}
          accessToken={accessToken}
          currentUser={currentUser}
          onUpdate={afterChange}
        />
      )}

      {wizardTarget && (
        <DynamicWizard
          open
          onClose={() => setWizardTarget(null)}
          wizardId={wizardTarget.wizardId}
          configItemId={wizardTarget.configItemId}
          companyId={companyId}
          sowId={focusedSow?.id}
          consultingFirmId={currentUser?.consultingFirmId || currentUser?.firmId || ''}
          userRole={viewerRole}
          onComplete={() => { setWizardTarget(null); afterChange(); }}
        />
      )}

      {viewerRole === 'consultant' && focusedEngagementId && (
        <ScopeReconcileDialog
          open={showReconcile}
          onClose={() => setShowReconcile(false)}
          companyId={companyId}
          accessToken={accessToken}
          target={{ type: 'engagement', engagementId: focusedEngagementId, label: focusedSow?.name || focusedSow?.title || 'this engagement' }}
          engagements={sows.map((s: any) => ({ id: s.id, name: s.name || s.title, status: s.status }))}
          onDone={afterChange}
        />
      )}

      <Dialog open={!!guidedCategory} onOpenChange={(open) => { if (!open) { setGuidedCategory(null); setGuidedFocusQuestionId(null); afterChange(); } }}>
        <DialogContent className="sm:max-w-[95vw] !w-[95vw] !h-[95vh] max-h-[95vh] p-0 overflow-y-auto">
          <DialogTitle className="sr-only">Guided Mode</DialogTitle>
          <DialogDescription className="sr-only">Answer the category's guided questions.</DialogDescription>
          {guidedCategory && (
            <GuidedMode
              companyId={companyId}
              category={guidedCategory}
              categoryName={formatCategoryLabel(guidedCategory)}
              configItems={guidedItems}
              accessToken={accessToken}
              currentUser={currentUser}
              focusQuestionId={guidedFocusQuestionId ?? undefined}
              onComplete={() => { setGuidedCategory(null); setGuidedFocusQuestionId(null); afterChange(); }}
              onExit={() => { setGuidedCategory(null); setGuidedFocusQuestionId(null); afterChange(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
